import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = [
        "form",
        "titleInput",
        "catalogSelect",
        "titleError",
        "catalogError",
        "submitButton",
        "submitText",
        "submitSpinner",
        "deleteModal",
        "deleteButton",
        "confirmDeleteButton",
        "catalogDeleteModal",
        "catalogDeleteButton",
        "catalogConfirmDeleteButton",
        "recurrentJobCategoryDeleteModal",
        "recurrentJobCategoryDeleteForm",
        "recurrentJobCategoryConfirmDeleteButton",
        // Catalog Category Selector targets
        "catalogCategorySelectorForm",
        "catalogSelect",
        "categorySelect",
        "customerCategoryCatalogSection",
        "customerCategoryCatalogInfo",
        "categorySection",
        "catalogCategorySubmitButton",
        "loadingModal",
    ];
    static values = {
        showCatalogSelection: Boolean,
        categoryId: String,
        catalogName: String,
        customerCategoryCatalogId: String,
        recurrentJobId: String,
        ajaxUrl: String,
    };

    connect() {
        console.log("JobProfessionCategory controller connected");
    }

    submit(event) {
        event.preventDefault();

        // Reset error states
        this.clearErrors();

        // Get form data
        const formData = new FormData(this.formTarget);
        const title = formData.get("title").trim();

        // Validate form
        let hasErrors = false;

        if (!title || title.length < 3) {
            this.showError("titleError", "Der Titel muss mindestens 3 Zeichen lang sein.");
            hasErrors = true;
        }

        if (this.showCatalogSelectionValue) {
            const catalog = formData.get("catalog");
            if (!catalog) {
                this.showError("catalogError", "Bitte wählen Sie einen Catalog aus.");
                hasErrors = true;
            }
        }

        if (hasErrors) {
            return;
        }

        // Show loading state
        this.setLoadingState(true);

        // Submit form
        this.formTarget.submit();
    }

    clearErrors() {
        this.titleErrorTarget.textContent = "";
        this.titleInputTarget.classList.remove("jlls-form-input-error");

        if (this.showCatalogSelectionValue) {
            this.catalogErrorTarget.textContent = "";
            this.catalogSelectTarget.classList.remove("jlls-form-select-error");
        }
    }

    showError(elementId, message) {
        let errorElement;
        let inputElement;

        if (elementId === "titleError") {
            errorElement = this.titleErrorTarget;
            inputElement = this.titleInputTarget;
            inputElement.classList.add("jlls-form-input-error");
        } else if (elementId === "catalogError" && this.showCatalogSelectionValue) {
            errorElement = this.catalogErrorTarget;
            inputElement = this.catalogSelectTarget;
            inputElement.classList.add("jlls-form-select-error");
        }

        if (errorElement) {
            errorElement.textContent = message;
        }
    }

    setLoadingState(loading) {
        if (loading) {
            this.submitButtonTarget.disabled = true;
            this.submitTextTarget.textContent = "Wird erstellt...";
            this.submitSpinnerTarget.classList.remove("tw-hidden");
        } else {
            this.submitButtonTarget.disabled = false;
            this.submitTextTarget.textContent = "Kategorie erstellen";
            this.submitSpinnerTarget.classList.add("tw-hidden");
        }
    }

    showDeleteModal(event) {
        event.preventDefault();

        // Setze die categoryId und catalogName aus dem geklickten Button
        const button = event.currentTarget;
        const categoryId = button.dataset.jobProfessionCategoryCategoryIdValue;
        const catalogName = button.dataset.jobProfessionCategoryCatalogNameValue;

        // Setze die Werte im Modal
        if (this.hasDeleteModalTarget) {
            this.deleteModalTarget.dataset.jobProfessionCategoryCategoryIdValue = categoryId;
            this.deleteModalTarget.dataset.jobProfessionCategoryCatalogNameValue = catalogName;
            this.categoryIdValue = categoryId;
            this.catalogNameValue = catalogName;
            this.deleteModalTarget.classList.remove("tw-hidden");
        }
    }

    hideDeleteModal() {
        if (this.hasDeleteModalTarget) {
            this.deleteModalTarget.classList.add("tw-hidden");
        }
    }

    confirmDelete() {
        if (this.hasConfirmDeleteButtonTarget) {
            this.confirmDeleteButtonTarget.disabled = true;
            this.confirmDeleteButtonTarget.textContent = "Wird gelöscht...";
        }

        // Erstelle ein verstecktes Formular für die POST-Anfrage
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `/_/admin/job-profession-category/categories/${this.categoryIdValue}/delete-customer-category-catalog/${this.catalogNameValue}`;

        // CSRF-Token hinzufügen (falls vorhanden)
        const csrfToken = document.querySelector('meta[name="csrf-token"]');
        if (csrfToken) {
            const csrfInput = document.createElement("input");
            csrfInput.type = "hidden";
            csrfInput.name = "_token";
            csrfInput.value = csrfToken.getAttribute("content");
            form.appendChild(csrfInput);
        }

        document.body.appendChild(form);
        form.submit();
    }

    showCatalogDeleteModal(event) {
        event.preventDefault();

        // Setze die customerCategoryCatalogId aus dem geklickten Button
        const button = event.currentTarget;
        const customerCategoryCatalogId = button.dataset.jobProfessionCategoryCustomerCategoryCatalogIdValue;

        // Setze die Werte im Modal
        if (this.hasCatalogDeleteModalTarget) {
            this.catalogDeleteModalTarget.dataset.jobProfessionCategoryCustomerCategoryCatalogIdValue =
                customerCategoryCatalogId;
            this.customerCategoryCatalogIdValue = customerCategoryCatalogId;
            this.catalogDeleteModalTarget.classList.remove("tw-hidden");
        }
    }

    hideCatalogDeleteModal() {
        if (this.hasCatalogDeleteModalTarget) {
            this.catalogDeleteModalTarget.classList.add("tw-hidden");
        }
    }

    confirmCatalogDelete() {
        if (this.hasCatalogConfirmDeleteButtonTarget) {
            this.catalogConfirmDeleteButtonTarget.disabled = true;
            this.catalogConfirmDeleteButtonTarget.textContent = "Wird gelöscht...";
        }

        // Erstelle ein verstecktes Formular für die POST-Anfrage
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `/_/admin/job-profession-category/customer-category-catalogs/${this.customerCategoryCatalogIdValue}/delete`;

        // CSRF-Token hinzufügen (falls vorhanden)
        const csrfToken = document.querySelector('meta[name="csrf-token"]');
        if (csrfToken) {
            const csrfInput = document.createElement("input");
            csrfInput.type = "hidden";
            csrfInput.name = "_token";
            csrfInput.value = csrfToken.getAttribute("content");
            form.appendChild(csrfInput);
        }

        document.body.appendChild(form);
        form.submit();
    }

    // Recurrent Job Category functions
    showRecurrentJobCategoryDeleteModal(event) {
        event.preventDefault();

        // Setze die recurrentJobId und categoryId aus dem geklickten Button
        const button = event.currentTarget;
        const recurrentJobId = button.dataset.recurrentJobId;
        const categoryId = button.dataset.categoryId;

        // Setze die Werte im Modal
        if (this.hasRecurrentJobCategoryDeleteModalTarget) {
            this.recurrentJobIdValue = recurrentJobId;
            this.categoryIdValue = categoryId;
            this.recurrentJobCategoryDeleteModalTarget.classList.remove("tw-hidden");
        }
    }

    hideRecurrentJobCategoryDeleteModal() {
        if (this.hasRecurrentJobCategoryDeleteModalTarget) {
            this.recurrentJobCategoryDeleteModalTarget.classList.add("tw-hidden");
        }
    }

    confirmRecurrentJobCategoryDelete() {
        if (this.hasRecurrentJobCategoryConfirmDeleteButtonTarget) {
            this.recurrentJobCategoryConfirmDeleteButtonTarget.disabled = true;
            this.recurrentJobCategoryConfirmDeleteButtonTarget.textContent = "Wird gelöscht...";
        }

        // Erstelle ein verstecktes Formular für die POST-Anfrage
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `/_/admin/job-profession-category/recurrent-jobs/${this.recurrentJobIdValue}/categories/${this.categoryIdValue}/delete`;

        // CSRF-Token hinzufügen (falls vorhanden)
        const csrfToken = document.querySelector('meta[name="csrf-token"]');
        if (csrfToken) {
            const csrfInput = document.createElement("input");
            csrfInput.type = "hidden";
            csrfInput.name = "_token";
            csrfInput.value = csrfToken.getAttribute("content");
            form.appendChild(csrfInput);
        }

        document.body.appendChild(form);
        form.submit();
    }

    // Catalog Category Selector functions
    onCatalogChange(event) {
        const catalogName = event.target.value;
        const contentDistributorId = event.target.options[event.target.selectedIndex].dataset.contentDistributorId;

        if (catalogName) {
            // Zeige Customer Category Catalog Info
            this.customerCategoryCatalogSectionTarget.style.display = "block";
            this.categorySectionTarget.style.display = "block";

            // Lade Kategorien für den ausgewählten Catalog
            this.loadCategories(catalogName, contentDistributorId);
        } else {
            this.customerCategoryCatalogSectionTarget.style.display = "none";
            this.categorySectionTarget.style.display = "none";
            this.catalogCategorySubmitButtonTarget.disabled = true;
        }
    }

    onCategoryChange(event) {
        this.catalogCategorySubmitButtonTarget.disabled = !event.target.value;
    }

    loadCategories(catalogName, contentDistributorId) {
        // Zeige Loading Modal
        this.loadingModalTarget.classList.remove("tw-hidden");

        // Echter API-Aufruf
        const url = new URL(this.ajaxUrlValue, window.location.origin);
        url.searchParams.append("catalogName", catalogName);

        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                // Zeige Customer Category Catalog Info
                if (data.customerCategoryCatalog) {
                    this.customerCategoryCatalogInfoTarget.textContent = `${data.customerCategoryCatalog.name} (ID: ${data.customerCategoryCatalog.id})`;
                } else {
                    this.customerCategoryCatalogInfoTarget.textContent = "Kein Customer Category Catalog gefunden";
                }

                // Leere und fülle Category Select
                this.categorySelectTarget.innerHTML = '<option value="">Bitte wählen Sie eine Kategorie...</option>';
                data.categories.forEach((category) => {
                    const option = document.createElement("option");
                    option.value = category.id;
                    option.textContent = category.title;
                    this.categorySelectTarget.appendChild(option);
                });

                this.categorySelectTarget.disabled = false;
                this.loadingModalTarget.classList.add("tw-hidden");
            })
            .catch((error) => {
                console.error("Error loading categories:", error);

                // Zeige Fehlermeldung
                this.customerCategoryCatalogInfoTarget.textContent = "Fehler beim Laden der Kategorien";

                this.categorySelectTarget.innerHTML = '<option value="">Fehler beim Laden der Kategorien</option>';
                this.categorySelectTarget.disabled = true;
                this.loadingModalTarget.classList.add("tw-hidden");
            });
    }
}
